import { SuggestionData } from 'ckeditor5-premium-features'
import { useSuggestionsShallowSelector } from 'components/WorkspaceEditor/SuggestionStore'
import { TrackedChange } from 'models/changes'
import VirtualList, { ListRef } from 'rc-virtual-list'
import React, { memo, useMemo, useRef } from 'react'
import { SuggestionCardWrapper } from '../SuggestionCardWrapper'
import { useContainerHeight } from './hooks/useContainerHeight'
import { useHighlightVirtualizedCard } from './hooks/useHighlightVirtualizedCard'
import { useResetScrollOnRequest } from './hooks/useResetScrollOnRequest'
import { useScreenObserver } from './hooks/useScreenObserver'

interface VirtualizedSuggestionListProps {
    suggestions: SuggestionData[]
    trackChangesData: Record<string, TrackedChange>
    containerHeight?: number
    isCustomFilterActive?: boolean
}

const DEFAULT_CONTAINER_HEIGHT = 1000
const HEIGHT_BUFFER = 90
const HEIGHT_BUFFER_REDUCTION = 40
const ITEM_HEIGHT = 90
const SMALL_SCREEN_HEIGHT_THRESHOLD = 740
const SMALL_SCREEN_WIDTH_THRESHOLD = 1485
const ZOOM_THRESHOLD = 1

/**
 * VirtualizedSuggestionList component renders a list of suggestions
 * @param {VirtualizedSuggestionListProps} props - The properties for the component.
 * @return {JSX.Element} The rendered virtualized suggestion list.
 */
export const VirtualizedSuggestionList = memo(
    ({
        suggestions,
        trackChangesData,
        isCustomFilterActive = false,
    }: VirtualizedSuggestionListProps) => {
        const listRef = useRef<ListRef>(null)
        const activeCardFilter = useSuggestionsShallowSelector(
            (s) => s.suggestionCardFilter
        )

        const {
            height: screenHeight,
            width: screenWidth,
            zoom,
        } = useScreenObserver()
        const containerHeight = useContainerHeight(
            '[data-testid="tree-layout"]',
            DEFAULT_CONTAINER_HEIGHT
        )
        const viewportHeight = useMemo(() => {
            const isSmallScreen =
                screenHeight < SMALL_SCREEN_HEIGHT_THRESHOLD &&
                screenWidth < SMALL_SCREEN_WIDTH_THRESHOLD
            const isSmallZoomedScreen = isSmallScreen && zoom > ZOOM_THRESHOLD
            const buffer = isSmallZoomedScreen
                ? HEIGHT_BUFFER - HEIGHT_BUFFER_REDUCTION
                : HEIGHT_BUFFER
            return containerHeight + buffer
        }, [containerHeight, screenHeight, screenWidth, zoom])

        useHighlightVirtualizedCard(suggestions, listRef)
        useResetScrollOnRequest(listRef, isCustomFilterActive)

        return (
            <VirtualList
                data={suggestions}
                height={viewportHeight}
                itemHeight={ITEM_HEIGHT}
                itemKey="id"
                virtual
                ref={listRef}
            >
                {(suggestion: SuggestionData, index: number) => (
                    <div key={suggestion.id}>
                        <SuggestionCardWrapper
                            suggestion={suggestion}
                            trackChangesData={trackChangesData}
                            index={index}
                            isNotDefaultSuggestionCardFilter={
                                isCustomFilterActive
                            }
                            suggestionCardFilter={activeCardFilter}
                        />
                    </div>
                )}
            </VirtualList>
        )
    }
)
